import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ─── API Response Types ─── */
export interface CustomerLookupData {
	fullName: string;
	address: string;
	accountType: string;
	customerOutstandingDebtBalance: number;
	minimumPayment: number;
	isMd: boolean;
}

export interface VendRequest {
	customerNumber: string;
	amount: number;
	channel: string;
	latitude?: number;
	longitude?: number;
	phoneNumber: string;
	type: string;
	callbackUrl: string;
}

export interface TokenInfo {
	token: string;
	tokenDec: string;
	vendedAmount: string;
	unit: string;
	description: string;
	drn: string;
}

export interface PaymentReceipt {
	reference: string;
	bankReceiptNo: string | null;
	paidAtUtc: string;
	customerName: string;
	customerAccountNumber: string;
	customerAddress: string;
	customerPhoneNumber: string;
	customerMeterNumber: string | null;
	accountType: string;
	tariffRate: number;
	units: number;
	vatRate: number;
	vatAmount: number;
	electricityAmount: number;
	outstandingDebt: number;
	debtPayable: number;
	totalAmountPaid: number;
	currency: string;
	channel: string;
	status: string;
	tokens: TokenInfo[];
	serviceCharge: number;
	discountBonus: number;
}

export interface PaymentData {
	paymentId: number;
	isPending: boolean;
	isMd: boolean;
	externalReference: string;
	bankReceiptNo: string | null;
	reference: string;
	paidAtUtc: string;
	customerName: string;
	customerAccountNumber: string;
	customerAddress: string;
	customerPhoneNumber: string;
	customerMeterNumber: string | null;
	customerId: number;
	accountType: string;
	tariffRate: number;
	units: number;
	vatRate: number;
	vatAmount: number;
	electricityAmount: number;
	outstandingDebt: number;
	debtPayable: number;
	totalAmountPaid: number;
	currency: string;
	channel: string;
	status: string;
	paymentTypeName: string;
	shouldUpgrade: boolean;
	receipt: PaymentReceipt;
	token: TokenInfo | null;
	paymentDetails: {
		reference: string;
		checkoutUrl: string;
	};
}

export interface ApiResponse<T> {
	data: T;
	isSuccess: boolean;
	message: string;
}

/* ─── Base URL Config ─── */
// Toggle this flag to switch between sandbox and production APIs
const USE_PRODUCTION_API = false;

export const BASE_URL = USE_PRODUCTION_API
	? "https://api.blumenos.com/"
	: "https://api.blumenos.com/";

/* ─── RTK Query API Slice ─── */
export const blumenApi = createApi({
	reducerPath: "blumenApi",
	baseQuery: fetchBaseQuery({
		baseUrl: BASE_URL,
	}),
	endpoints: (builder) => ({
		/* Customer Lookup (Quick Vend verify) */
		lookupCustomer: builder.query<
			ApiResponse<CustomerLookupData>,
			{ reference: string; type: string }
		>({
			query: ({ reference, type }) => ({
				url: "self-service/customers/lookup",
				params: { reference, type },
			}),
		}),

		/* Vend Electricity */
		vendElectricity: builder.mutation<
			ApiResponse<any>,
			VendRequest
		>({
			query: (body) => ({
				url: "self-service/vend",
				method: "POST",
				body,
			}),
		}),

		/* Get Token / Payment Status */
		getToken: builder.mutation<
			ApiResponse<PaymentData>,
			{ reference: string }
		>({
			query: (body) => ({
				url: "self-service/get-token",
				method: "POST",
				body,
			}),
		}),
	}),
});

export const {
	useLazyLookupCustomerQuery,
	useVendElectricityMutation,
	useGetTokenMutation,
} = blumenApi;
